import moment from 'moment';
import { fromJS, List, Map } from 'immutable';
import storage from '@synergycms_core/utils/storage';
import { AVAIL_LIFETIME } from '../../../config/constants';

import actions from '../../actions';
import { hashSpliter } from '@synergycms_core/utils';

/**
 *
 * @param hotelId
 * @param hotelCode
 * @param hotelName
 * @param checkinDate
 * @param stayLength
 * @param occups
 * @param currencyCode
 * @param clientCurrencyCode
 * @param transactionId
 * @param countryCode
 * @param clientIp
 * @param availCode
 * @param promotionCode
 * @param giftVoucher
 * @param companyId
 * @param refererCode
 * @param companyCode
 * @param loyaltyUserId
 * @param syn2LoyaltyUser
 * @param blocks
 * @param system
 * @returns {*|Immutable.Map<any, any>|Immutable.Map<string, *>}
 */
export const createReservationRequest = (
  hotelId,
  hotelCode,
  hotelName,
  checkinDate,
  stayLength,
  occups,
  currencyCode,
  clientCurrencyCode,
  transactionId,
  countryCode,
  clientIp,
  availCode = null,
  promotionCode = null, // promotion_code
  giftVoucher = null, // gift_voucher
  companyId = null,
  refererCode = null,
  companyCode = null,
  loyaltyUserId = null,
  loyalty = null,
  blocks = false,
  system = false
) =>
  Map({
    temporary: true,
    status: 'temporary',
    booking_date: moment().format(),
    checkin: checkinDate,
    checkout: moment(checkinDate)
      .add(stayLength, 'days')
      .format('YYYY-MM-DD'),
    hotel_id: hotelId,
    hotel_code: hotelCode,
    hotel_name: hotelName,
    currency: Map({
      code: currencyCode
    }),
    channel: companyCode ? 'Corporate' : 'web',
    company_code: companyCode,
    loyalty_user_id: loyaltyUserId,
    loyalty : loyalty,
    avail_code: availCode,
    promotion_code: promotionCode,
    gift_voucher: giftVoucher,
    company_id: companyId,
    referer_code: refererCode,
    transaction_id: transactionId,
    blocks:
      blocks ||
      occups.reduce((result, occup, key) => {
        const overrideOccup = occup.set("childsAge", occup.get("childsAge",List()).map((val)=> val.get("age")));
        return result.push(Map({
          idx: +key + 1,
          services: List()
        }).merge(overrideOccup));

      }, List()),
    room_set: List(),
    customer: Map({
      type: 'guest',
      name: null,
      surname: null,
      country: null,
      state: null,
      address: null,
      email: null,
      phone: null,
      lang: null,
      web_type: null,
      currency_code: {
        code: clientCurrencyCode
      },
      notes: null,
      company_name: null,
      source_url: window.location.href,
      td: null,
      tds: [],
      tdd: [],
      tdf: [],
      td_history: null,
    }),
    origin: Map({
      country: countryCode,
      ip: clientIp,
      browser: navigator.userAgent,
      utc_offset: new Date().getTimezoneOffset(),
      browser_name: system && system.get('name', ''),
      device : system && system.get('device', ''),
      os : system && system.get('os', ''),
      viewport : system && system.get('viewport', '')
    })
  });

/**
 *
 * @param reservation
 * @param serviceAvail
 * @returns {*}
 */
export const roomAsServiceBreakDown = (reservation, serviceAvail) => {
  // Emplenem el room_set necessari per fer habitacions + paquet a NodeJS
  reservation = reservation.set(
    'room_set',
    reservation.get('blocks').reduce((roomSets, block) => {
      if (parseInt(block.get('service_id'), 10) > 0) {
        roomSets = roomSets.push(
          Map({
            idx: block.get('idx'),
            room_id: block.get('room_id'),
            service_id: block.get('service_id')
          })
        );
      }
      return roomSets;
    }, List())
  );
  // Desglossem preus també per Hab + Paquet
  reservation = reservation.set(
    'blocks',
    reservation.get('blocks').reduce((blocks, block) => {
      const serviceId = block.get('service_id');
      if (parseInt(serviceId, 10) > 0) {
        const serviceKey = block.get('services').findKey(s => s.get('service_id') === serviceId);
        const serviceAvailKey = serviceAvail.findKey(s => s.get('id') === serviceId);
        const service = serviceAvail.getIn([serviceAvailKey]);

        block = block
          .set('price', block.get('room_board_price'))
          .set('price_tax', block.get('room_board_price_tax'))
          .setIn(['services', serviceKey, 'price'],
            (service.get('has_closed_price') || service.get('has_closed_price_board') ?
              block.get('service_price') :
              service.get('price')));
      }
      return blocks.push(block);
    }, List())
  );

  return reservation;
};

/**
 *
 * @param reservationRequest
 * @param reservation
 * @returns {boolean}
 */
export const checkSameReservation = (reservationRequest, reservation) =>
  reservationRequest &&
  reservation.get('blocks', List()).size > 0 &&
  !reservationRequest.get('blocks').some(requestBlock => {
    // some different?
    const blockIndex = reservation.get('blocks').findIndex(b => b.get('idx') === requestBlock.get('idx'));
    if (blockIndex === -1) {
      return true;
    } else {
      const block = reservation.getIn(['blocks', blockIndex]);
      if (
        !(
          block.get('room_id') === requestBlock.get('room_id') &&
          block.get('board_id') === requestBlock.get('board_id') &&
          block.get('occup_group_id') === requestBlock.get('occup_group_id') &&
          block.get('refundable') === requestBlock.get('refundable') &&
          block.get('price_tax') === requestBlock.get('price_tax')
        )
      ) {
        return true;
      }

      const requestBlockServices = requestBlock.get('services', List());
      const blockServices = block.get('services', List());

      if (requestBlockServices.size === 0 && blockServices.size === 0) {
        return false;
      } else if (requestBlockServices.size !== blockServices.size) {
        return true;
      } else {
        return requestBlockServices.some(
          requestBlockService =>
            blockServices.findKey(s => s.get('service_id') === requestBlockService.get('service_id')) === -1
        );
      }
    }
  });

/**
 *
 * @param dispatch
 * @param getState
 * @param reservationTrackingId
 * @param recoveredBookingBlocks
 * @returns {*}
 */
export const recoverReservation = (dispatch, getState, reservationTrackingId, recoveredBookingBlocks) => {
  return new Promise((resolve, reject) => {
    const storageReservation = storage.get('reservation-' + reservationTrackingId);
    let storageReservationRequest = recoveredBookingBlocks || fromJS(storage.get('last_reservation_request'));
    
    const corporateStore = getState().getIn(['customer', 'corporate'], Map());
    const companyCode = corporateStore.get('afiliado', false) || corporateStore.get('avail_code', false) || false;

    const hotelId = getState().getIn(['customer', 'current_hotel']);
    const queryParams = getState().getIn(['hotels', hotelId, 'params', 'query_params']);

    const getCurrentStepCode = () =>
      getState()
        .getIn(['customer', 'step', 'current_step'])
        .get('code');

    const restoreReservation = () => {
      const corporatePayForms = storage.getLocal('corporate_payforms');
      dispatch(actions.customer.reservationRequest.set(storageReservationRequest));
      dispatch(
        actions.customer.corporate.setPayforms(corporatePayForms && companyCode ? fromJS(corporatePayForms) : List())
      );
    };

    const resetReservationRequest = (recoveredBookingBlocks = undefined) => {
      storage.unset('last_reservation_request');
      dispatch(
        actions.customer.reservationRequest.initialize(recoveredBookingBlocks && recoveredBookingBlocks.get('blocks'))
      );
      dispatch(actions.customer.reservation.set(Map()));
      storage.unset('reservation-' + reservationTrackingId);
      reservationTrackingId = undefined;
    };

    const checkBookingDate = bookingDate =>
      getCurrentStepCode() === 'confirmation' ||
      (bookingDate && moment(bookingDate) >= moment().subtract(AVAIL_LIFETIME / 60 / 1000, 'minutes'));

    if (queryParams) {
      const isValidReservationRequest =
        storageReservationRequest !== null &&
        checkReservationRequestParams(storageReservationRequest, queryParams) &&
        checkBookingDate(storageReservationRequest.get('booking_date', false));

      if (recoveredBookingBlocks && isValidReservationRequest) {
        // RECOVER RESERVATION REQUEST BY MAIL OR PARAMS
        resetReservationRequest(recoveredBookingBlocks);
        if (getCurrentStepCode() === 'customerForm') {
          dispatch(actions.customer.reservation.postAsync()).then(() => {
            resolve(getState().getIn(['customer', 'reservation', 'tracking_id'], ''));
            return true;
          });
        }
      } else if (reservationTrackingId && reservationTrackingId.length > 0) {
        if (
          !getState().getIn(['customer', 'reservation', 'tracking_id'], false) &&
          isValidReservationRequest &&
          storageReservation !== null &&
          storageReservation.tracking_id === parseInt(reservationTrackingId, 10)
        ) {
          // RECOVER RESERVATION
          let reservation = fromJS(storageReservation);
          if (checkBookingDate(reservation.get('booking_date')) || reservation.get('status') === 'confirmed') {
            dispatch(actions.customer.reservation.set(reservation));
          } else {
            resetReservationRequest();
            dispatch(actions.notifications.send('error', '_%error al recuperar la reserva: Reserva caducada%_'));
          }
        } else if (
          !getState().getIn(['customer', 'reservation', 'tracking_id'], false) ||
          parseInt(reservationTrackingId, 10) !== getState().getIn(['customer', 'reservation', 'tracking_id'], false)
        ) {
          resetReservationRequest();
        }
      }

      //RECOVER RESERVATION REQUEST
      if (isValidReservationRequest) {
        restoreReservation();
      } else {
        resetReservationRequest();
      }

      let storeReservation = getState().getIn(['customer', 'reservation']);

      // Comprovació TPV EPG
      const tpvPaymentStatus = hashSpliter(window.location.hash)[3] || false;
      if (
        tpvPaymentStatus === 'EPG:OK' &&
        storeReservation.get('status') === 'confirmed' &&
        storeReservation.getIn(['payment', 'payform_code']) === 'op'
      ) {
        dispatch(actions.customer.reservation.set(storeReservation.setIn(['payment', 'state'], 'payed')));
        storeReservation = getState().getIn(['customer', 'reservation']);
      }

      // Comprovació nova reserva tinguent una reserva confirmada al store
      if (
        getCurrentStepCode() !== 'confirmation' &&
        reservationTrackingId &&
        storeReservation.get('status') === 'confirmed' &&
        !(
          (['op', 'amazon_pay'].includes(storeReservation.getIn(['payment', 'payform_code']))) &&
          storeReservation.getIn(['payment', 'state']) === 'pending'
        )
      ) {
        resetReservationRequest();
      }
    }

    resolve(reservationTrackingId);
    return true;
  });
};

/**
 *
 * @param reservationRequest
 * @param queryParams
 * @returns {*|boolean}
 */
const checkReservationRequestParams = (reservationRequest, queryParams) => {
  const checkIn = queryParams.get('date');
  const checkOut = moment(checkIn)
    .add(queryParams.get('stay_length'), 'days')
    .format('YYYY-MM-DD');

  return (
    reservationRequest &&
    reservationRequest.get('hotel_id') === queryParams.get('hotelId') &&
    reservationRequest.get('checkin') === checkIn &&
    reservationRequest.get('checkout') === checkOut &&
    validateAvailCode(
      reservationRequest.get('avail_code'),
      reservationRequest.get('company_code'),
      queryParams.get('avail_code')
    ) &&
    reservationRequest.get("gift_voucher", null) === queryParams.get('gift_voucher', null) &&
    reservationRequest.get("promotion_code", null) === queryParams.get('promotion_code', null) &&
    reservationRequest.get("referer_code", null) === queryParams.get('referer_code', null) &&
    reservationRequest.get("company_id", null) === queryParams.get('company_id', null) &&
    reservationRequest.get('blocks').size === queryParams.get('occups').size &&
    !reservationRequest.get('blocks').some(block => {
      // block no seleccionat encara
      if (!block.get('room_id', false)) {
        return false;
      }
      const guests = queryParams.getIn(['occups', (block.get('idx') - 1).toString()]);
      return !(guests.get('adults') === block.get('adults') && guests.get('childs', 0) === (block.get('childs') + block.get('babies',0)));
    })
  );
};

const validateAvailCode = (resAvailCode, resCompanyCode, queryAvailCode) => {
  if (queryAvailCode) {
    return resAvailCode === queryAvailCode || resCompanyCode === queryAvailCode;
  } else {
    return resAvailCode === queryAvailCode && resCompanyCode === queryAvailCode;
  }
};

export const generateBlockFromParams = store => {
  const selectionAvail = store.getIn(['customer', 'params', 'params', 'selection_avail']);
  const hotelId = store.getIn(['customer', 'current_hotel']);
  const rateSelected = store.getIn(['customer', 'rate_selected']);
  const roomsAvail = store.getIn([
    'avail',
    hotelId,
    store.getIn(['hotels', hotelId, 'params', rateSelected === 'loyalty' ? 'string_loyalty_query_params' : 'string_query_params']),
    'response'
  ]);

  const blocks = roomsAvail.reduce((blocks, segment, keySegment) => {
    const selectedRoomRate = selectionAvail.get(keySegment, Map());
    const room = segment.get(selectedRoomRate.get('room', false), false);
    const rateAvail = room && room.getIn(['boards', selectedRoomRate.get('board'), selectedRoomRate.get('type')], null);
    const invalidBoard = rateAvail && rateAvail.get('board_code') === '14' && room.getIn(['free_breakfast']);
    const serviceId = room && room.get('service_id', null);
    if (!invalidBoard && rateAvail) {
      return blocks.push(Map({
        idx: keySegment + 1,
        occup_group_id: +selectedRoomRate.get('occup'),
        adults: room.get('adults'),
        childs: room.get('childs'),
        room_id: room.get('room_id'),
        service_id: serviceId,
        board_id: selectedRoomRate.get('board'),
        refundable: rateAvail.get('refundable'),
        ref_type: selectedRoomRate.get('type'),
        price: rateAvail.get('price'),
        price_tax: rateAvail.get('price_tax'),
        discount_amount: rateAvail.get('discount_amount'),
        services: (serviceId ? List([Map({ service_id: serviceId, price: 0, quantity: 1 })]) : List()),
        preferences: store.getIn(['customer', 'room_preferences']),
        // Compatibilitat Hab + Paquet
        room_board_price: rateAvail.get('room_board_price', rateAvail.get('price')),
        room_board_price_tax: rateAvail.get('room_board_price_tax', rateAvail.get('price_tax')),
        service_price: rateAvail.get('service_price', 0)
      }));
    } else {
      return blocks.push(Map({
        idx: keySegment + 1,
        services: List()
      }));
    }
  }, new List());
  return blocks;
};

export const recoverBookingBlocksFromAvailMail = (getState, queryObj) => {
  const store = getState();
  const hotelId = store.getIn(['customer', 'current_hotel']);
  const roomsAvail = store.getIn([
    'avail',
    hotelId,
    store.getIn(['hotels', hotelId, 'params', 'string_query_params']),
    'response'
  ]);

  return (
    queryObj.getIn(['resRequest']) &&
    queryObj.getIn(['resRequest']).every(block => {
      const currentRoomPrice = roomsAvail.getIn([
        0,
        block.get('room_id'),
        'boards',
        block.get('board_id'),
        block.get('ref_type'),
        'price_tax'
      ]);
      return (
        currentRoomPrice === block.get('price_tax') &&
        moment(block.get('checkin')).isSameOrAfter(moment().format('YYYY-MM-DD'))
      );
    }) &&
    queryObj.getIn(['resRequest'])
  );
};

export const hasCancellationFeesIn15Days = reservation => {
  const checkin = moment(reservation.get('checkin'));
  const firstPenalty = reservation.getIn(['penalties', 0]);
  const release = firstPenalty.get('release');
  const daysRelease = release / 24;
  const releaseDay = checkin.clone().subtract(daysRelease, 'd');
  const daysDiff = releaseDay.diff(moment(), 'd');
  return daysDiff <= 15; //daysRelease;
};



